import Alpine from 'alpinejs';

Alpine.data('fileUpload', (config) => ({
    selectedFiles: new Map(),
    dropZoneId: config.dropZoneId,
    fileInputId: config.fileInputId,
    filePreviewId: config.filePreviewId,
    submitButtonId: config.submitButtonId,
    accept: config.accept,
    maxSize: config.maxSize,
    multiple: config.multiple,
    name: config.name,
    standalone: config.standalone,

    init() {
        console.log('FileUpload component initializing with config:', config);

        // Bind methods to this component instance
        this.handleFileSelect = this.handleFileSelect.bind(this);
        this.handleDragOver = this.handleDragOver.bind(this);
        this.handleDragLeave = this.handleDragLeave.bind(this);
        this.handleDrop = this.handleDrop.bind(this);
        this.removeFile = this.removeFile.bind(this);
        this.submitFiles = this.submitFiles.bind(this);

        // Initialize the file preview container
        const previewContainer = document.getElementById(this.filePreviewId);
        console.log('Preview container element:', previewContainer);
        if (previewContainer) {
            previewContainer.style.display = 'block';
            console.log('Preview container initialized');
        } else {
            console.error('Preview container not found with ID:', this.filePreviewId);
        }

        // Log the component's element
        console.log('Component element:', this.$el);
        console.log('Component data:', this.$data);

        this.$watch('selectedFiles', () => {
            console.log('selectedFiles changed:', this.selectedFiles);
            this.updateSubmitButton();
            // Ensure preview container is visible when files are added
            if (previewContainer) {
                const shouldShow = this.selectedFiles.size > 0;
                console.log('Setting preview container visibility:', shouldShow);
                previewContainer.style.display = shouldShow ? 'block' : 'none';
            }
        });

        // If not standalone, intercept form submission and add files to FormData
        if (!this.standalone) {
            const form = this.$el.closest('form');
            console.log('Parent form:', form);
            if (form) {
                form.addEventListener('submit', (e) => {
                    console.log('Form submit event triggered');

                    // If we have files, we need to prevent default submission
                    // and submit with FormData to include files
                    if (this.selectedFiles.size > 0) {
                        e.preventDefault();
                        this.submitFormWithFiles(form, e);
                    }
                    // If no files, let form submit normally
                });
            }
        }
    },

    // New method to handle form submission with files
    submitFormWithFiles(form, originalEvent) {
        console.log('Submitting form with files:', this.selectedFiles.size);

        const formData = new FormData(form);

        // Remove any existing file inputs to avoid conflicts
        const existingFileInputs = form.querySelectorAll(`input[name="${this.name}"]`);
        existingFileInputs.forEach(input => {
            if (input.id !== this.fileInputId) {
                formData.delete(this.name);
            }
        });

        // Add our selected files to FormData
        this.selectedFiles.forEach(file => {
            console.log('Adding file to form data:', file.name);
            formData.append(this.name, file);
        });

        // Log all form data for debugging
        console.log('FormData contents:');
        for (let [key, value] of formData.entries()) {
            if (value instanceof File) {
                console.log(key, 'File:', value.name, 'Size:', value.size, 'Type:', value.type);
            } else {
                console.log(key, value);
            }
        }

        // Additional validation before sending
        const files = Array.from(formData.getAll(this.name));
        console.log('Files being uploaded:', files.length);
        files.forEach((file, index) => {
            console.log(`File ${index}:`, {
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified
            });
        });

        // Submit via fetch
        fetch(form.action, {
            method: form.method,
            body: formData,
            headers: {
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                'X-Requested-With': 'XMLHttpRequest',
                'Accept': 'application/json'
            }
        })
        .then(response => {
            console.log('Response status:', response.status);
            console.log('Response headers:', response.headers);

            if (response.ok) {
                return response.json().then(data => {
                    if (data.success && data.redirect) {
                        window.location.href = data.redirect;
                    } else {
                        // Fallback to orders page
                        window.location.href = '/orders';
                    }
                });
            } else {
                // Handle validation errors
                if (response.status === 422) {
                    return response.json().then(data => {
                        console.error('Validation errors:', data.errors);
                        // Display validation errors
                        let errorMessage = 'Validation errors:\n';
                        for (let field in data.errors) {
                            errorMessage += `${field}: ${data.errors[field].join(', ')}\n`;
                        }
                        alert(errorMessage);
                    });
                } else {
                    // Other errors - get the response text for debugging
                    return response.text().then(text => {
                        console.error('Server response:', text);
                        throw new Error(`Form submission failed with status ${response.status}`);
                    });
                }
            }
        })
        .catch(error => {
            console.error('Form submission error:', error);
            alert('Error submitting form. Please check the console for details and try again.');
        });
    },

    formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    validateFile(file) {
        console.log('Validating file:', file.name);
        const validTypes = this.accept.split(',');
        const maxSize = this.maxSize * 1024 * 1024;
        const fileExt = '.' + file.name.split('.').pop().toLowerCase();

        console.log('File validation:', {
            validTypes,
            maxSize,
            fileExt,
            fileSize: file.size
        });

        if (!validTypes.includes(fileExt)) {
            throw new Error(`Invalid file type. Only ${validTypes.join(', ')} files are allowed.`);
        }

        if (file.size > maxSize) {
            throw new Error(`File size exceeds ${this.maxSize}MB limit.`);
        }

        console.log('File validation passed');
        return true;
    },

    handleFileSelect(event) {
        console.log('File select event triggered');
        const files = Array.from(event.target.files);
        console.log('Selected files:', files);

        if (files.length > 0) {
            files.forEach(file => this.addFile(file));
            // Force Alpine to update the view
            this.$nextTick(() => {
                console.log('Next tick after file selection');
                const previewContainer = document.getElementById(this.filePreviewId);
                console.log('Preview container after selection:', previewContainer);
                if (previewContainer) {
                    previewContainer.style.display = 'block';
                    console.log('Preview container shown');
                }
            });
        }
        event.target.value = ''; // Reset input to allow selecting the same file again
    },

    handleDragOver(event) {
        console.log('Drag over event');
        event.preventDefault();
        event.currentTarget.classList.add('border-indigo-500');
    },

    handleDragLeave(event) {
        console.log('Drag leave event');
        event.preventDefault();
        event.currentTarget.classList.remove('border-indigo-500');
    },

    handleDrop(event) {
        console.log('Drop event triggered');
        event.preventDefault();
        event.currentTarget.classList.remove('border-indigo-500');
        const files = Array.from(event.dataTransfer.files);
        console.log('Dropped files:', files);

        if (files.length > 0) {
            files.forEach(file => this.addFile(file));
            // Force Alpine to update the view
            this.$nextTick(() => {
                console.log('Next tick after drop');
                const previewContainer = document.getElementById(this.filePreviewId);
                console.log('Preview container after drop:', previewContainer);
                if (previewContainer) {
                    previewContainer.style.display = 'block';
                    console.log('Preview container shown after drop');
                }
            });
        }
    },

    addFile(file) {
        console.log('Adding file:', file.name);
        try {
            this.validateFile(file);
            const fileId = crypto.randomUUID();
            console.log('Generated file ID:', fileId);
            this.selectedFiles.set(fileId, file);
            console.log('Current selected files:', Array.from(this.selectedFiles.entries()));

            // Force Alpine to update the view
            this.$nextTick(() => {
                console.log('Next tick after adding file');
                const previewContainer = document.getElementById(this.filePreviewId);
                console.log('Preview container after adding file:', previewContainer);
                if (previewContainer) {
                    previewContainer.style.display = 'block';
                    console.log('Preview container shown after adding file');
                }
            });
        } catch (error) {
            console.error('Error adding file:', error);
            alert(error.message);
        }
    },

    removeFile(fileId) {
        console.log('Removing file with ID:', fileId);
        this.selectedFiles.delete(fileId);
        console.log('Remaining files:', Array.from(this.selectedFiles.entries()));

        // Force Alpine to update the view
        this.$nextTick(() => {
            console.log('Next tick after removing file');
            const previewContainer = document.getElementById(this.filePreviewId);
            console.log('Preview container after removing file:', previewContainer);
            if (previewContainer) {
                const shouldShow = this.selectedFiles.size > 0;
                previewContainer.style.display = shouldShow ? 'block' : 'none';
                console.log('Preview container visibility after removal:', shouldShow);
            }
        });
    },

    updateSubmitButton() {
        console.log('Updating submit button');
        if (this.standalone) {
            const submitButton = document.getElementById(this.submitButtonId);
            console.log('Submit button element:', submitButton);
            if (submitButton) {
                submitButton.disabled = this.selectedFiles.size === 0;
                console.log('Submit button disabled:', submitButton.disabled);
            }
        }
    },

    async submitFiles() {
        console.log('Submit files triggered');
        if (!this.standalone) {
            console.log('Not in standalone mode, skipping submit');
            return;
        }

        const form = this.$el.closest('form');
        console.log('Form for submission:', form);
        if (!form) {
            console.error('No form found for submission');
            return;
        }

        if (this.selectedFiles.size === 0) {
            console.log('No files to submit');
            alert('Please select at least one file to upload.');
            return;
        }

        const formData = new FormData(form);
        this.selectedFiles.forEach(file => {
            console.log('Adding file to form data:', file.name);
            formData.append(this.name, file);
        });

        try {
            const submitButton = document.getElementById(this.submitButtonId);
            if (submitButton) {
                submitButton.disabled = true;
                submitButton.textContent = 'Uploading...';
                console.log('Submit button updated for upload');
            }

            console.log('Sending form data to:', form.action);
            const response = await fetch(form.action, {
                method: form.method,
                body: formData,
                headers: {
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Upload failed');
            }

            console.log('Upload successful, reloading page');
            window.location.reload();
        } catch (error) {
            console.error('Upload error:', error);
            alert('Failed to upload files. Please try again.');
            const submitButton = document.getElementById(this.submitButtonId);
            if (submitButton) {
                submitButton.disabled = false;
                submitButton.textContent = 'Upload';
                console.log('Submit button reset after error');
            }
        }
    }
}));
